import { create } from 'zustand';
import { api, setAccessToken, setViewAsHooks } from '../lib/api.js';
import { queryClient } from '../lib/queryClient.js';
import { viewAsApi } from '../api/viewAs.api.js';
import { useAuthStore } from './authStore.js';

/* ===========================================================================
 * VIEW AS — the console's half (server: packages/api/src/routes/viewAs.routes.ts).
 *
 * WHERE THE ADMINISTRATOR'S SESSION IS KEPT: nowhere new. It is the httpOnly
 * refresh cookie, which starting a view never touches. The view-as token
 * replaces the in-memory access token and nothing else, so leaving is one
 * `/auth/refresh` — no password, and no second token held in script reach.
 *
 * A RELOAD ENDS THE VIEW. The view-as token lives only in memory, so a reload
 * boots from the cookie as it always does and the administrator is back as
 * themselves. That is the safer of the two choices (nothing that can act as
 * someone else is ever written to storage) and the simpler (no second boot
 * path). The server records the start; the token simply expires.
 *
 * Every answer cached as one identity is dropped when it changes, both ways:
 * the administrator's data must not show under the person's banner, nor the
 * person's after the administrator is back.
 * ======================================================================== */

export type ViewAsEnd = 'exit' | 'expired';

interface ViewAsState {
  /** Who is being viewed, while a view is on. */
  viewing: { userId: string; name: string; expiresAt: string } | null;
  /** How the last view ended, until the banner has said so. */
  ended: ViewAsEnd | null;
  start: (person: { id: string; name: string }) => Promise<void>;
  exit: (how?: ViewAsEnd) => Promise<void>;
  acknowledge: () => void;
}

let timer: ReturnType<typeof setTimeout> | null = null;
let leaving: Promise<void> | null = null;

export const useViewAsStore = create<ViewAsState>((set, get) => ({
  viewing: null,
  ended: null,
  async start(person) {
    const session = await viewAsApi.start(person.id);
    queryClient.clear();
    setAccessToken(session.accessToken);
    setViewAsHooks({
      lost: () => void get().exit('expired'),
    });
    useAuthStore.setState({ user: session.user, status: 'authenticated' });
    set({ viewing: { userId: person.id, name: person.name, expiresAt: session.expiresAt }, ended: null });
    const msLeft = Date.parse(session.expiresAt) - Date.now();
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void get().exit('expired'), Math.max(0, msLeft));
  },
  async exit(how = 'exit') {
    if (!get().viewing) return;
    // One exit at a time: a timer and a 401 can both fire for the same expiry.
    if (leaving) return leaving;
    leaving = (async () => {
      if (timer) clearTimeout(timer);
      timer = null;
      // Recorded with the view-as token, before it is dropped. Best effort.
      await viewAsApi.exit().catch(() => undefined);
      setViewAsHooks(null);
      setAccessToken(null);
      queryClient.clear();
      set({ viewing: null, ended: how });
      try {
        const own = await api.refresh();
        useAuthStore.setState({ user: own.user, status: 'authenticated' });
      } catch {
        useAuthStore.setState({ user: null, status: 'unauthenticated' });
      }
    })().finally(() => {
      leaving = null;
    });
    return leaving;
  },
  acknowledge() {
    set({ ended: null });
  },
}));

/* Signing out while viewing ends the view too (the cookie is gone with it). */
useAuthStore.subscribe((s) => {
  if (s.status !== 'unauthenticated' || !useViewAsStore.getState().viewing) return;
  if (timer) clearTimeout(timer);
  timer = null;
  setViewAsHooks(null);
  useViewAsStore.setState({ viewing: null, ended: null });
});
